import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { jwtDecode } from 'jwt-decode';
import { useHistory } from 'react-router-dom';

interface DecodedToken {
    exp: number;
}

type ModalState = 'hidden' | 'warning' | 'expired';

const SessionTimeoutModal: React.FC = () => {
    const [modalState, setModalState] = useState<ModalState>('hidden');
    const [timeLeft, setTimeLeft] = useState<number | null>(null);
    const history = useHistory();

    const checkTokenExpiration = useCallback(() => {
        const token = localStorage.getItem('jwt');
        if (!token) return;

        try {
            const decoded = jwtDecode<DecodedToken>(token);
            const expirationTime = decoded.exp * 1000; // convert to ms
            const currentTime = Date.now();
            const timeRemaining = expirationTime - currentTime;

            const warningTime = 5 * 60 * 1000; // 5 minutes

            if (timeRemaining <= 0) {
                // Session already expired
                setModalState('expired');
            } else if (timeRemaining <= warningTime) {
                // Session about to expire
                setModalState('warning');
                setTimeLeft(Math.ceil(timeRemaining / 60000)); // minutes
            } else {
                setModalState('hidden');
            }
        } catch (error) {
            console.error('Token inválido:', error);
        }
    }, []);

    useEffect(() => {
        checkTokenExpiration();
        const intervalId = setInterval(checkTokenExpiration, 30000); // check every 30s
        return () => clearInterval(intervalId);
    }, [checkTokenExpiration]);

    const handleDismissWarning = () => {
        setModalState('hidden');
    };

    const handleGoToLogin = () => {
        localStorage.removeItem('jwt');
        setModalState('hidden');
        history.replace('/login');
    };

    // --- Expired modal ---
    if (modalState === 'expired') {
        return (
            <Modal show centered backdrop="static" keyboard={false}>
                <Modal.Header>
                    <Modal.Title>⚠️ Sesión expirada</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Tu sesión ha expirado. Por favor inicia sesión nuevamente para continuar.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={handleGoToLogin}>
                        Ir al login
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    // --- Warning modal ---
    if (modalState === 'warning') {
        return (
            <Modal show centered backdrop="static" keyboard={false}>
                <Modal.Header>
                    <Modal.Title>¿Sigues ahí?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Tu sesión está a punto de expirar
                        {timeLeft !== null ? ` en menos de ${timeLeft} minuto${timeLeft !== 1 ? 's' : ''}` : ''}.
                    </p>
                    <p>Por favor guarda tu trabajo o recarga la página para continuar.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleGoToLogin}>
                        Ir al login
                    </Button>
                    <Button variant="primary" onClick={handleDismissWarning}>
                        Estoy aquí
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    return null;
};

export default SessionTimeoutModal;
